import { useRef, useCallback } from 'react';

/**
 * Hook to handle mobile-specific interactions: Long Press and Double Tap
 */
export const useMobileInteractions = ({
  isMobile,
  onLongPress,
  onDoubleTap,
  longPressDelay = 600,
  doubleTapDelay = 300
}) => {
  const longPressTimer = useRef(null);
  const lastTap = useRef(0);
  const touchStartPos = useRef({ x: 0, y: 0 });

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };

    // Setup Long Press
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      onLongPress(e);
      longPressTimer.current = null;
    }, longPressDelay);

    // Setup Double Tap
    const now = Date.now();
    if (now - lastTap.current < doubleTapDelay) {
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      onDoubleTap(e);
      lastTap.current = 0; // reset
    } else {
      lastTap.current = now;
    }
  }, [isMobile, onLongPress, onDoubleTap, longPressDelay, doubleTapDelay]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile || !longPressTimer.current) return;
    
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchStartPos.current.x);
    const dy = Math.abs(touch.clientY - touchStartPos.current.y);
    
    // If the finger moves significantly, cancel the long press
    if (dx > 10 || dy > 10) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, [isMobile]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};
